import Navbar from '../components/Navbar';
import './About.css';

const features = [
  {
    title: 'Browse & filter by category',
    desc: 'Narrow the catalogue by app type — Power Apps, Native Apps or Web Applications — so the right solution surfaces in seconds.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
        <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
      </svg>
    ),
  },
  {
    title: 'Live title search',
    desc: 'Start typing and the grid updates instantly — no page reload. Search stacks with category and status filters for surgical precision.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
    ),
  },
  {
    title: 'Rich app detail pages',
    desc: 'Every app has its own page: full-width hero, plain-language description, mockup gallery with lightbox zoom, and a link to the functional document.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/>
        <line x1="9" y1="21" x2="9" y2="9"/>
      </svg>
    ),
  },
  {
    title: 'Technology tags',
    desc: 'Each app shows only the technologies it actually uses — React, React Native, .NET, Power Apps, Business Central or SharePoint.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
      </svg>
    ),
  },
  {
    title: 'Active & concept toggle',
    desc: 'Switch between live apps and work-in-progress concepts from the settings menu — control exactly what you share with each audience.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
      </svg>
    ),
  },
  {
    title: 'Secure Azure AD login',
    desc: 'Gated behind Microsoft Entra ID — only Astena-authenticated users can browse. Nothing is visible before sign-in.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    ),
  },
];

const stats = [
  { value: '40+', label: 'Solutions built' },
  { value: '3',   label: 'App categories' },
  { value: '8',   label: 'Technologies' },
  { value: '30+', label: 'Clients served' },
];

const steps = [
  {
    number: '01',
    title: 'Sign in securely',
    desc: 'Log in with your Astena Microsoft account. Azure Entra ID handles authentication — no separate credentials needed.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
        <polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/>
      </svg>
    ),
  },
  {
    number: '02',
    title: 'Browse the catalogue',
    desc: 'Filter by category, toggle concept apps on or off, or type a name in the search bar. The grid responds immediately.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
        <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
      </svg>
    ),
  },
  {
    number: '03',
    title: 'Dive into the details',
    desc: 'Open any app for the full picture: description, mockup gallery, technology stack and — where available — the functional document.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
      </svg>
    ),
  },
];

function About() {
  return (
    <div className="page-wrapper">
      <Navbar />
    <div className="about-page">

      {/* ── Hero ─────────────────────────────────── */}
      <div className="about-hero">
        <p className="about-hero-eyebrow">Astena App Showcase</p>
        <h1 className="about-hero-title">
          Every solution we build,<br />
          <span>in one place.</span>
        </h1>
        <p className="about-hero-body">
          The Astena Showcase is an internal portal that brings our entire
          application portfolio together — from active client solutions to
          concepts in progress. Browse, filter and dive into the details of
          every app we've built, all in one branded, always-up-to-date catalogue.
        </p>
      </div>

      {/* ── Stats strip ──────────────────────────── */}
      <div className="about-stats">
        {stats.map(s => (
          <div key={s.label} className="about-stat">
            <span className="about-stat-value">{s.value}</span>
            <span className="about-stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── Feature highlights ───────────────────── */}
      <div className="about-features">
        <p className="about-section-label">What it does</p>
        <h2 className="about-section-title">Everything you need to present our work</h2>
        <div className="about-features-grid">
          {features.map(f => (
            <div key={f.title} className="about-feature-card">
              <div className="about-feature-icon">{f.icon}</div>
              <h3 className="about-feature-title">{f.title}</h3>
              <p className="about-feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── How it works ─────────────────────────── */}
      <div className="about-steps">
        <p className="about-section-label">How it works</p>
        <h2 className="about-section-title">From login to full picture in three steps</h2>
        <div className="about-steps-row">
          {steps.map(s => (
            <div key={s.number} className="about-step">
              <div className="about-step-icon">{s.icon}</div>
              <span className="about-step-number">{s.number}</span>
              <h3 className="about-step-title">{s.title}</h3>
              <p className="about-step-desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Under the hood ───────────────────────── */}
      <div className="about-tech">
        <p className="about-section-label">Under the hood</p>
        <h2 className="about-section-title">Simple to maintain, easy to grow</h2>

        <div className="about-tech-grid">
          <div className="about-tech-card">
            <h3 className="about-tech-card-title">One file to add a new app</h3>
            <p className="about-tech-card-body">
              The entire catalogue is driven by a single <code>config.json</code>. Adding a
              new application means appending one JSON object with a name, title,
              description, category, status and technology tags. No code changes,
              no deployments — just update the file and the app appears in the grid.
            </p>
          </div>

          <div className="about-tech-card">
            <h3 className="about-tech-card-title">Images straight from Azure Storage</h3>
            <p className="about-tech-card-body">
              Card thumbnails, hero images and mockup galleries are stored in Azure
              Blob Storage and served directly from there. Drop a <code>card.png</code> or{' '}
              <code>card_up.png</code> into the app's container folder and the showcase
              picks it up automatically — no manual wiring needed.
            </p>
          </div>

          <div className="about-tech-card">
            <h3 className="about-tech-card-title">React frontend, zero backend</h3>
            <p className="about-tech-card-body">
              The portal is a fully client-side React application. There's no
              dedicated API server to maintain — configuration is static JSON,
              assets live in Blob Storage, and authentication is handled entirely
              by the Microsoft Entra ID (MSAL) SDK in the browser.
            </p>
          </div>

          <div className="about-tech-card">
            <h3 className="about-tech-card-title">Secure by design</h3>
            <p className="about-tech-card-body">
              Every route is protected by Azure Entra ID. Unauthenticated visitors
              see only a login screen — nothing leaks before sign-in. SAS tokens
              scope storage access per session, so assets are never publicly
              reachable without a valid user context.
            </p>
          </div>
        </div>
      </div>

    </div>
    </div>
  );
}

export default About;
